import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';

const GENRES = [
  {
    title: 'Children',
    subtitle: 'CHILDREN & YOUNG ADULTS',
    slug: 'children-young-adults',
  },
  {
    title: 'History',
    subtitle: 'HISTORY/SOCIOLOGY',
    slug: 'history-sociology',
  },
  {
    title: 'Novels',
    subtitle: 'NOVELS',
    slug: 'novels',
  },
  {
    title: 'Poetry',
    subtitle: 'POETRY',
    slug: 'poetry',
  },
  {
    title: 'Short Stories',
    subtitle: 'SHORT STORIES',
    slug: 'short-stories',
  },
];

const VERTEX_SHADER = `
  attribute vec2 position;
  void main() {
    gl_Position = vec4(position, 0.0, 1.0);
  }
`;

// Dense, billowy, atmospheric clouds with natural matte sky shading
const FRAGMENT_SHADER = `
  precision highp float;
  uniform vec2 u_resolution;
  uniform float u_time;

  vec2 hash2(vec2 p) {
    p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));
    return -1.0 + 2.0 * fract(sin(p) * 43758.5453123);
  }

  float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(mix(dot(hash2(i + vec2(0.0, 0.0)), f - vec2(0.0, 0.0)),
                   dot(hash2(i + vec2(1.0, 0.0)), f - vec2(1.0, 0.0)), u.x),
               mix(dot(hash2(i + vec2(0.0, 1.0)), f - vec2(0.0, 1.0)),
                   dot(hash2(i + vec2(1.0, 1.0)), f - vec2(1.0, 1.0)), u.x), u.y);
  }

  mat2 rot = mat2(0.80, -0.60, 0.60, 0.80);

  float fbm(vec2 p) {
    float f = 0.0;
    f += 0.5000 * noise(p); p = rot * p * 2.02;
    f += 0.2500 * noise(p); p = rot * p * 2.03;
    f += 0.1250 * noise(p); p = rot * p * 2.01;
    f += 0.0625 * noise(p); p = rot * p * 2.04;
    f += 0.03125 * noise(p); p = rot * p * 2.01;
    f += 0.015625 * noise(p);
    return f;
  }

  void main() {
    vec2 uv = gl_FragCoord.xy / u_resolution.xy;
    vec2 aspect = vec2(u_resolution.x / u_resolution.y, 1.0);
    vec2 p = uv * aspect * 1.5;

    // Natural, vibrant matte sky blue (clear cerulean blue sky)
    vec3 skyTop = vec3(0.18, 0.48, 0.82);     // Rich natural sky blue
    vec3 skyBottom = vec3(0.48, 0.72, 0.90);  // Soft cerulean horizon
    vec3 skyColor = mix(skyBottom, skyTop, uv.y);

    // Smooth drifting cloud motion
    vec2 drift = vec2(u_time * 0.024, u_time * 0.006);
    vec2 q = p + drift;

    // Domain warping for distinct, rolling cumulus formations
    vec2 r1 = vec2(fbm(q + vec2(0.0, 0.0)), fbm(q + vec2(4.8, 1.6)));
    vec2 r2 = vec2(fbm(q * 1.4 + vec2(2.1, 4.4)), fbm(q * 1.4 + vec2(1.7, 7.8)));
    
    // Main cloud noise + secondary puff layer + detail layer for richer cumulus volume
    float cloudNoise1 = fbm(q + 1.25 * r1);
    float cloudPuff = fbm(q * 1.5 + 0.85 * r2 + vec2(1.8, -1.2)) * 0.35;
    float cloudDetail = fbm(q * 2.3 + 0.6 * r2) * 0.32;
    float combined = cloudNoise1 + cloudPuff + cloudDetail;

    // Increased cloudiness (~70-75% cloud coverage with blue sky still clearly visible)
    float density = smoothstep(-0.16, 0.34, combined);

    // Volumetric cloud shading: soft silver-tinted underside & bright white sunlit tops
    vec3 cloudShadow = vec3(0.87, 0.91, 0.96);     // Soft cool silver underbelly
    vec3 cloudHighlight = vec3(1.0, 1.0, 1.0);     // Crisp chalky white highlights
    vec3 cloudColor = mix(cloudShadow, cloudHighlight, smoothstep(0.0, 0.38, combined));

    // Composite: richer, fluffier clouds while preserving blue sky
    vec3 finalColor = mix(skyColor, cloudColor, density * 0.98);

    gl_FragColor = vec4(finalColor, 1.0);
  }
`;

export default function CloudShaderGenres({ categories = [] }) {
  const canvasRef = useRef(null);
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  const displayGenres =
    categories.length > 0
      ? categories.slice(0, 6).map((cat) => ({
          title: cat.name,
          slug: cat.slug,
          link:
            cat.hasNew === false && cat.hasSecondhand
              ? `/secondhand?category=${cat.slug}`
              : `/new?category=${cat.slug}`,
        }))
      : GENRES.map((g) => ({
          title: g.title,
          slug: g.slug,
          link: `/new?category=${g.slug}`,
        }));

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { rootMargin: '200px 0px' } // start rendering a bit before it scrolls in
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !isVisible) return;

    let animId;
    let gl = null;

    try {
      gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    } catch {
      gl = null;
    }

    if (gl) {
      const createShader = (type, source) => {
        const shader = gl.createShader(type);
        gl.shaderSource(shader, source);
        gl.compileShader(shader);
        return shader;
      };

      const vs = createShader(gl.VERTEX_SHADER, VERTEX_SHADER);
      const fs = createShader(gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
      const program = gl.createProgram();
      gl.attachShader(program, vs);
      gl.attachShader(program, fs);
      gl.linkProgram(program);
      gl.useProgram(program);

      const buffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.bufferData(
        gl.ARRAY_BUFFER,
        new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
        gl.STATIC_DRAW
      );

      const posLocation = gl.getAttribLocation(program, 'position');
      gl.enableVertexAttribArray(posLocation);
      gl.vertexAttribPointer(posLocation, 2, gl.FLOAT, false, 0, 0);

      const uResolution = gl.getUniformLocation(program, 'u_resolution');
      const uTime = gl.getUniformLocation(program, 'u_time');

      const resize = () => {
        const width = canvas.clientWidth || 1200;
        const height = canvas.clientHeight || 200;
        if (canvas.width !== width || canvas.height !== height) {
          canvas.width = width;
          canvas.height = height;
          gl.viewport(0, 0, width, height);
        }
      };

      resize();
      window.addEventListener('resize', resize);

      const start = performance.now();
      const render = () => {
        resize();
        const elapsed = (performance.now() - start) * 0.001;
        gl.uniform2f(uResolution, canvas.width, canvas.height);
        gl.uniform1f(uTime, elapsed);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
        animId = requestAnimationFrame(render);
      };

      render();

      return () => {
        cancelAnimationFrame(animId);
        window.removeEventListener('resize', resize);
      };
    } else {
      // High density 2D canvas fallback with abundant clouds
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const clouds = [
        { x: 60, y: 50, rx: 180, ry: 90, speed: 0.35 },
        { x: 260, y: 110, rx: 220, ry: 100, speed: 0.28 },
        { x: 500, y: 60, rx: 190, ry: 95, speed: 0.4 },
        { x: 740, y: 120, rx: 240, ry: 110, speed: 0.3 },
        { x: 980, y: 55, rx: 210, ry: 100, speed: 0.38 },
        { x: 1200, y: 105, rx: 230, ry: 105, speed: 0.32 },
        { x: 1440, y: 70, rx: 190, ry: 90, speed: 0.42 },
      ];

      const render2d = () => {
        const width = (canvas.width = canvas.clientWidth || 1200);
        const height = (canvas.height = canvas.clientHeight || 200);

        const skyGrad = ctx.createLinearGradient(0, 0, 0, height);
        skyGrad.addColorStop(0, '#226bb8');
        skyGrad.addColorStop(1, '#62a0d8');
        ctx.fillStyle = skyGrad;
        ctx.fillRect(0, 0, width, height);

        ctx.fillStyle = 'rgba(255, 255, 255, 0.94)';
        ctx.filter = 'blur(28px)';
        clouds.forEach((c) => {
          c.x += c.speed;
          if (c.x - c.rx > width + 150) c.x = -c.rx - 150;

          ctx.beginPath();
          ctx.ellipse(c.x, c.y, c.rx, c.ry, 0, 0, Math.PI * 2);
          ctx.ellipse(c.x + 50, c.y - 25, c.rx * 0.85, c.ry * 0.85, 0, 0, Math.PI * 2);
          ctx.ellipse(c.x - 45, c.y + 20, c.rx * 0.75, c.ry * 0.75, 0, 0, Math.PI * 2);
          ctx.ellipse(c.x + 20, c.y + 30, c.rx * 0.65, c.ry * 0.65, 0, 0, Math.PI * 2);
          ctx.fill();
        });
        ctx.filter = 'none';

        animId = requestAnimationFrame(render2d);
      };

      render2d();
      return () => cancelAnimationFrame(animId);
    }
  }, [isVisible]);

  return (
    <section ref={sectionRef} className="relative overflow-hidden border-y border-rule min-h-[160px] sm:min-h-[175px] md:min-h-[190px] flex flex-col justify-center items-center py-3 sm:py-4 my-6 sm:my-8">
      {/* 1. Live Cloud Shader Canvas (Blue Sky & White Fluffy Moving Clouds) */}
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      />

      {/* 2. Tactile Matte Paper/Linen Filter Overlay (adds textured matte feel while keeping blue sky & white clouds visible) */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none z-1"
        style={{
          backgroundImage: "url('/linen-texture.png')",
          backgroundRepeat: 'repeat',
          opacity: 0.30,
          mixBlendMode: 'overlay',
        }}
      />

      {/* 3. Soft Matte Finish Filter Layer (lightweight CSS gradient instead of expensive backdropFilter) */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none z-2"
        style={{
          background: 'linear-gradient(to bottom, rgba(222,216,207,0.06), rgba(222,216,207,0.10))',
        }}
      />

      {/* 4. Content Container floating over the textured matte cloud strip */}
      <div className="relative z-10 page-gutter mx-auto max-w-7xl w-full py-2 text-center">
        {/* Textured Postage Stamp Buttons Row with Generous Spacing */}
        <div className="flex flex-wrap items-center justify-center gap-8 sm:gap-12 md:gap-16 lg:gap-20 xl:gap-24 max-w-7xl mx-auto px-4">
          {displayGenres.map((g) => (
            <Link
              key={g.slug}
              to={g.link}
              aria-label={`Browse ${g.title} books`}
              className="relative w-24 h-24 sm:w-28 sm:h-28 md:w-30 md:h-30 lg:w-32 lg:h-32 flex items-center justify-center no-underline transform hover:scale-106 hover:-translate-y-1 active:scale-95 transition-all duration-200 cursor-pointer group drop-shadow-[0_8px_16px_rgba(0,0,0,0.22)] hover:drop-shadow-[0_14px_24px_rgba(0,0,0,0.30)]"
            >
              {/* Textured Perforated Postage Stamp Background */}
              <img
                src="/postage-stamp.png"
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none transition-transform duration-200 group-hover:brightness-[1.02]"
              />

              {/* Text in black positioned inside the stamp frame */}
              <span className="relative z-10 font-sans font-bold text-black text-xs sm:text-sm md:text-base tracking-tight leading-snug select-none text-center px-4 max-w-[85%]">
                {g.title}
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
